import { Component, inject, OnDestroy } from '@angular/core';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { DonutChartComponent, DonutChartData } from '../../../../../_shared/components/graphs/donut-chart/donut-chart.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { DateIntervalFilterData } from '../../../../../_shared/models';
import { OperationFrequentStops } from '../../../models/operation-frequent-stops.model';
import { startOfMonth, addMonths, endOfMonth } from 'date-fns';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';
import { DateInterval } from '../../../../../_shared/enums';
import { startOfDay, endOfDay, startOfWeek, endOfWeek } from 'date-fns';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { FilterService } from '../../../../../_shared/services';
import { Subject, Subscription } from 'rxjs';
import { DriverOperationsService } from '../../../services/driver-operations.service';
import { FrequentStopsLegendItem } from '../../../models/operation-frequent-stops.model';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-frequent-stops-graph',
  standalone: true,
  imports: [
    TranslocoPipe,
    DateFilterComponent,
    DonutChartComponent,
    GraphErrorStateComponent,
    EmptyStateComponent
],
  templateUrl: './frequent-stops-graph.component.html',
  styleUrl: './frequent-stops-graph.component.scss'
})
export class FrequentStopsGraphComponent implements OnDestroy {
  private driverOperationsService = inject(DriverOperationsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);

  private filterSubscription: Subscription;

  frequentStopsData: OperationFrequentStops[] = [];
  chartData: DonutChartData[] = [];
  isLoading: boolean = true;
  hasError: boolean = false;
  hoveredSegmentName: string | null = null;
  dateFilter: DateIntervalFilterData = { interval: DateInterval.Month } as DateIntervalFilterData;
  accountDateFilterRequest: AccountDateFilterRequest = {
    accountIds: [],
    timeFrom: startOfDay(new Date()),
    timeTo: endOfDay(new Date()),
    interval: 0
  };

  private readonly colors = ['#FF7F00', '#F4BC2B', '#FFE5A5'];
  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe((filter) => {
      // Guard: Skip if filter is empty/null
      if (Object.values(filter).every(x => x === null) || filter === null) {
        return;
      }
      this.accountDateFilterRequest.accountIds = filter.accountIds;
      this.getFrequentStops();
    });
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async getFrequentStops(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;

    try {
      const request = this.getFrequentStopsRequest();

      this.cancelSubject.next();
      const response = await this.driverOperationsService.getFrequentStops(request, this.cancelSubject);

      this.frequentStopsData = response.data ?? [];
      this.chartData = this.frequentStopsData.map((item, index) => ({
        name: item.location,
        value: item.stopCount,
        color: this.colors[index % this.colors.length]
      }));

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private getFrequentStopsRequest(): AccountDateFilterRequest {
    const currentDate = new Date();

    if (this.dateFilter.interval === DateInterval.Day) {
      return {
        ...this.accountDateFilterRequest,
        interval: this.dateFilter.interval,
        timeFrom: startOfDay(currentDate),
        timeTo: endOfDay(currentDate)
      };
    } else if (this.dateFilter.interval === DateInterval.Week) {
      return {
        ...this.accountDateFilterRequest,
        interval: this.dateFilter.interval,
        timeFrom: startOfWeek(currentDate),
        timeTo: endOfWeek(currentDate)
      };
    } else if (this.dateFilter.interval === DateInterval.Month) {
      return {
        ...this.accountDateFilterRequest,
        interval: this.dateFilter.interval,
        timeFrom: startOfMonth(addMonths(currentDate, -1)),
        timeTo: endOfMonth(currentDate)
      };
    } else {
      return {
        ...this.accountDateFilterRequest,
        interval: this.dateFilter.interval,
        timeFrom: startOfMonth(addMonths(currentDate, -6)),
        timeTo: endOfMonth(currentDate)
      };
    }
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.getFrequentStops();
  }

  get hasData(): boolean {
    return this.chartData.length > 0;
  }

  get legendItems(): FrequentStopsLegendItem[] {
    return this.chartData.map(item => ({
      label: item.name,
      color: item.color,
      value: item.value.toLocaleString('default', { maximumFractionDigits: 2 })
    }));
  }

  onSegmentHover(event: { percentage: number; name: string } | null): void {
    this.hoveredSegmentName = event?.name ?? null;
  }
}